/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useSpaceStore } from '@coze-foundation/space-store-adapter';
import { localStorageService } from '@coze-foundation/local-storage';

/** Resolve current workspace space id (e.g. on /apps where SpaceLayout is not mounted). */
export async function resolveWorkspaceSpaceId(): Promise<string | undefined> {
  const current = useSpaceStore.getState().space.id;
  if (current) {
    return current;
  }

  await useSpaceStore.getState().fetchSpaces(true);
  const store = useSpaceStore.getState();

  const storedId =
    (await localStorageService.getValueSync('workspace-spaceId')) ?? '';
  if (storedId && store.checkSpaceID(storedId)) {
    store.setSpace(storedId);
    return storedId;
  }

  const personalId = store.getPersonalSpaceID();
  if (personalId) {
    store.setSpace(personalId);
    return personalId;
  }

  const firstId = store.spaceList[0]?.id;
  if (firstId) {
    store.setSpace(firstId);
    return firstId;
  }

  return undefined;
}
